import { Column } from 'react-table'
import { FixMeAny, UnknownRecord } from 'types/global'
import { TableSize, TableVariant, TableVariantType } from '.'

// Drag and drop table header to reorder table column
export const getColumnDragAndDropObject = (
  tableRef: React.RefObject<HTMLTableElement> | null,
  onReorder: (selectedIndex: number, targetIndex: number) => void,
  withCheckbox?: boolean,
) => {
  const table = tableRef?.current
  let draggingEle: HTMLElement
  let draggingColumnIndex = 0
  let placeholder: HTMLElement
  let list: HTMLElement
  let isDraggingStarted = false

  // The current position of mouse relative to the dragging element
  let x: number
  let y: number

  if (!table) return null

  const cloneTable = () => {
    const rect = table.getBoundingClientRect()

    list = document.createElement('div')
    list.classList.add('clone-list')
    list.style.position = 'absolute'
    list.style.left = `${rect.left}px`
    list.style.top = `${rect.top}px`
    table.parentNode?.insertBefore(list, table)

    // Hide the original table
    table.style.visibility = 'hidden'

    // Get all cells
    const originalCells = formArray(table.querySelectorAll('tbody td'))

    const originalHeaderCells = formArray(table.querySelectorAll('th'))
    const numColumns = originalHeaderCells.length

    // Loop through the header cells
    originalHeaderCells.forEach((headerCell, headerIndex) => {
      const width = parseInt(window.getComputedStyle(headerCell).width)

      // Create a new table from given row
      const item = document.createElement('div')
      item.classList.add('draggable')

      const newTable = document.createElement('table')
      newTable.setAttribute('class', 'clone-table')
      newTable.style.width = `${width}px`

      // Header
      const th = headerCell.cloneNode(true)
      let newRow = document.createElement('tr')
      newRow.appendChild(th)
      newTable.appendChild(newRow)

      const cells = originalCells.filter((_, idx) => (idx - headerIndex) % numColumns === 0)

      cells.forEach((cell) => {
        const newCell = cell.cloneNode(true) as HTMLElement
        newCell.style.width = `${width}px`
        newRow = document.createElement('tr')
        newRow.appendChild(newCell)
        newTable.appendChild(newRow)
      })

      item.appendChild(newTable)
      list.appendChild(item)
    })
  }

  const onDragStartHandler = (e: MouseEvent) => {
    e.stopImmediatePropagation()
    const target = e.target as HTMLElement

    formArray(table.querySelectorAll('th')).forEach((htmlElement, index) => {
      if (htmlElement.contains(target)) {
        draggingColumnIndex = index
        return
      }
    })

    if (!isDraggingStarted) {
      isDraggingStarted = true

      cloneTable()

      draggingEle = formArray(list.children)[draggingColumnIndex]
      draggingEle.classList.add('dragging')

      // Determine the mouse position
      x = e.clientX - draggingEle.offsetLeft
      y = e.clientY - draggingEle.offsetTop

      // Let the placeholder take the height of dragging element
      // So the next element won't move to the left or right
      // to fill the dragging element space
      placeholder = document.createElement('div')
      placeholder.classList.add('placeholder')
      draggingEle.parentNode?.insertBefore(placeholder, draggingEle.nextSibling)
      placeholder.style.width = `${draggingEle.offsetWidth}px`
    }

    // Attach the listeners to `document`
    document.addEventListener('mousemove', mouseMoveHandler)
    document.addEventListener('mouseup', mouseUpHandler)
  }

  const mouseMoveHandler = (e: MouseEvent) => {
    e.stopImmediatePropagation()

    // Set position for dragging element
    draggingEle.style.position = 'absolute'

    draggingEle.style.top = `${draggingEle.offsetTop + e.clientY - y}px`
    draggingEle.style.left = `${draggingEle.offsetLeft + e.clientX - x}px`

    // Reassign the position of mouse
    x = e.clientX
    y = e.clientY

    // The current order
    // prevEle
    // draggingEle
    // placeholder
    // nextEle
    const prevEle = draggingEle.previousElementSibling
    const nextEle = placeholder.nextElementSibling

    // The dragging element is above the previous element
    // User moves the dragging element to the left
    if (prevEle && isOnLeft(draggingEle, prevEle)) {
      // The current order    -> The new order
      // prevEle              -> placeholder
      // draggingEle          -> draggingEle
      // placeholder          -> prevEle
      swap(placeholder, draggingEle)
      swap(placeholder, prevEle)
      return
    }

    // The dragging element is below the next element
    // User moves the dragging element to the bottom
    if (nextEle && isOnLeft(nextEle, draggingEle)) {
      // The current order    -> The new order
      // draggingEle          -> nextEle
      // placeholder          -> placeholder
      // nextEle              -> draggingEle
      swap(nextEle, placeholder)
      swap(nextEle, draggingEle)
    }
  }

  const mouseUpHandler = (e: MouseEvent) => {
    e.stopImmediatePropagation()
    // Remove the placeholder
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.removeChild(placeholder)
    }

    draggingEle.classList.remove('dragging')
    draggingEle.style.removeProperty('top')
    draggingEle.style.removeProperty('left')
    draggingEle.style.removeProperty('position')

    // Get the end index
    const targetIndex = formArray(list.children).indexOf(draggingEle)

    isDraggingStarted = false

    // Remove the `list` element
    list.parentNode?.removeChild(list)

    // Bring back the table
    table.style.removeProperty('visibility')

    const offSet = withCheckbox ? 1 : 0
    onReorder(draggingColumnIndex - offSet, targetIndex - offSet)
    // Remove the handlers of `mousemove` and `mouseup`
    document.removeEventListener('mousemove', mouseMoveHandler)
    document.removeEventListener('mouseup', mouseUpHandler)
  }

  return {
    intiColumnDragAndDrop: () => {
      table.querySelectorAll('th').forEach((headerCell, index) => {
        if (withCheckbox && index === 0) return
        const headerContent = headerCell.querySelector('div')
        headerContent?.setAttribute('draggable', 'true')
        headerContent?.classList.add('draggable')
        headerContent?.addEventListener('dragstart', onDragStartHandler)
      })
    },
    removeColumnDragAndDropListener: () => {
      table.querySelectorAll('th').forEach((headerCell, index) => {
        if (withCheckbox && index === 0) return
        headerCell.removeEventListener('dragstart', onDragStartHandler)
      })
    },
  }
}

// Swap two nodes
const swap = (nodeA: Element, nodeB: Element) => {
  const parentA = nodeA.parentNode
  const siblingA = nodeA.nextSibling === nodeB ? nodeA : nodeA.nextSibling

  // Move `nodeA` to before the `nodeB`
  nodeB.parentNode?.insertBefore(nodeA, nodeB)

  // Move `nodeB` to before the sibling of `nodeA`
  parentA?.insertBefore(nodeB, siblingA)
}

// Check if `nodeA` is on the left of `nodeB`
const isOnLeft = (nodeA: Element, nodeB: Element) => {
  // Get the bounding rectangle of nodes
  const rectA = nodeA.getBoundingClientRect()
  const rectB = nodeB.getBoundingClientRect()

  return rectA.left + rectA.width / 2 < rectB.left + rectB.width / 2
}

const formArray = (argv: NodeListOf<Element> | HTMLCollection) => Array.prototype.slice.call(argv) as Array<HTMLElement>

export const getReorderedColumns = (columns: Array<FixMeAny>, selectedIndex: number, targetIndex: number) => {
  if (selectedIndex === targetIndex) return columns

  const tempArray = [...columns]
  const selectedData = columns[selectedIndex]
  if (selectedIndex > targetIndex) {
    const firstPartArray = columns.slice(0, targetIndex)
    tempArray.splice(selectedIndex, 1)
    const secondPartArray = tempArray.slice(targetIndex)
    return [...firstPartArray, selectedData, ...secondPartArray]
  } else {
    const firstPartArray = tempArray.slice(0, targetIndex + 1)
    firstPartArray.splice(selectedIndex, 1)
    const secondPartArray = columns.slice(targetIndex + 1)
    return [...firstPartArray, selectedData, ...secondPartArray]
  }
}

export const orderColumns = <D extends UnknownRecord>(columns: Column<D>[], uniqueTableName: string) => {
  const storedOrder = localStorage.getItem(`${uniqueTableName}ColumnOrder`)
  const columnIds: Array<string> = storedOrder ? JSON.parse(storedOrder) : []
  const matchedColumns: Column<D>[] = []

  columnIds.forEach((columnId) => {
    const columnData = columns.find((columnData) => columnData.id === columnId)
    if (!columnData) return
    matchedColumns.push(columnData)
  })

  const unmatchedColumns = columns.filter((columnData) => !columnIds.some((id) => id === columnData.id))

  return [...matchedColumns, ...unmatchedColumns]
}

export const filterColumns = <D extends UnknownRecord>(columns: Column<D>[], uniqueTableName: string) => {
  const storedFilter = localStorage.getItem(`${uniqueTableName}VisibleColumns`)
  const visibleColumns: string[] = storedFilter ? JSON.parse(storedFilter) : []

  return columns.filter((column) => {
    const { isDisabled } = column as Column<D> & { isDisabled: boolean }
    return visibleColumns.some((columnId: string) => columnId === column.id) || isDisabled
  })
}

export const scrollbarWidth = () => {
  const scrollDiv = document.createElement('div')
  scrollDiv.setAttribute('style', 'width: 100px; height: 100px; overflow: scroll; position:absolute; top:-9999px;')
  document.body.appendChild(scrollDiv)
  const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth
  document.body.removeChild(scrollDiv)
  return scrollbarWidth
}

export const resolveTableHeight = (size: TableSize) => {
  if (size === 'small') {
    return 200
  }
  if (size === 'large') {
    return 800
  }
  return 400
}

export const isDefaultTable = (tableVariant?: TableVariant) => tableVariant && tableVariant === TableVariantType.default
export const isInlineTable = (tableVariant?: TableVariant) => tableVariant && tableVariant === TableVariantType.inline
